import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareText, Download, LogOut, ShieldCheck, Loader2, FileText, LockKeyhole } from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import MfaCard from "../components/MfaCard";

function Card({ title, subtitle, icon: Icon, children, testid }) {
    return (
        <section
            data-testid={testid}
            className="rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-6 sm:p-7 space-y-5"
        >
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-zinc-300 stroke-[1.75]" />
                </div>
                <div>
                    <h2 className="text-base md:text-lg font-semibold text-zinc-100">{title}</h2>
                    <p className="text-[13px] text-zinc-500 mt-0.5 leading-relaxed">{subtitle}</p>
                </div>
            </div>
            {children}
        </section>
    );
}

function SmsAlertsCard() {
    const [prefs, setPrefs] = useState(null);
    const [phone, setPhone] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);

    const touched = useRef(false);
    const savingRef = useRef(false);
    const loadRevision = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (savingRef.current) return;
            const revision = ++loadRevision.current;
            setError("");
            try {
                const { data } = await api.get("/notifications/sms");
                if (cancelled || revision !== loadRevision.current) return;
                setPrefs(data);
                if (!touched.current) setPhone(data.phone || "");
            } catch (_err) {
                if (!cancelled && revision === loadRevision.current) setError("Could not load text-alert settings.");
            }
        };
        load();
        window.addEventListener("focus", load);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", load);
        };
    }, [retry]);

    async function save(enabled) {
        if (savingRef.current) return;
        savingRef.current = true;
        loadRevision.current += 1;
        setSaving(true);
        try {
            const { data } = await api.patch("/notifications/sms", { enabled, phone });
            setPrefs(data);
            setPhone(data.phone || "");
            touched.current = false;
            toast.success(enabled ? "Text alerts turned on" : "Text alerts turned off");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update text alerts");
            if (e.response?.status === 403) setRetry((n) => n + 1);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }

    const enabled = !!prefs?.enabled;
    const phoneChanged = phone !== (prefs?.phone || "");
    return (
        <Card
            testid="account-sms-card"
            icon={MessageSquareText}
            title="Text alerts"
            subtitle="Paid add-on · new leads and website build updates."
        >
            {error ? (
                <div role="alert" data-testid="account-sms-error" className="text-[13px] text-red-300">
                    {error}{" "}<button type="button" onClick={() => setRetry((n) => n + 1)}
                        data-testid="account-sms-retry-button" className="underline hover:text-red-100 transition-colors">Retry</button>
                </div>
            ) : !prefs ? (
                <p data-testid="account-sms-loading" className="text-[13px] text-zinc-500">Loading…</p>
            ) : !prefs.addon_enabled ? (
                <p data-testid="account-sms-addon-locked" className="flex items-center gap-2 text-[13px] text-zinc-400">
                    <LockKeyhole className="h-4 w-4 shrink-0" /> SMS alerts are not included with this portal.
                </p>
            ) : <>
            <div className="space-y-2">
                <label htmlFor="sms-phone" className="block text-[12px] text-zinc-400 font-medium">
                    Mobile number
                </label>
                <input
                    id="sms-phone"
                    type="tel"
                    disabled={saving}
                    value={phone}
                    onChange={(e) => {
                        touched.current = true;
                        setPhone(e.target.value);
                    }}
                    placeholder="(602) 555-0123"
                    data-testid="account-sms-phone-input"
                    className="w-full sm:max-w-xs h-11 rounded-md bg-zinc-950/60 border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-500"
                />
            </div>
            <p className="text-[12px] text-zinc-500 leading-relaxed" data-testid="account-sms-consent-text">
                By turning on text alerts you agree to receive automated new-lead and website build-status notifications from WebJesters at this
                number. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to cancel, START to resume.
                Consent is not a condition of purchase. See our <Link to="/privacy" data-testid="account-sms-privacy-link" className="underline">Privacy Policy</Link>.
            </p>
            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    disabled={saving || !prefs}
                    onClick={() => save(!enabled || phoneChanged)}
                    data-testid="account-sms-toggle-button"
                    className={`h-10 px-4 rounded-md text-[13.5px] font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-60 ${
                        enabled
                            ? "bg-zinc-900 text-zinc-200 border border-zinc-700 hover:bg-zinc-800"
                            : "bg-zinc-50 text-zinc-950 hover:bg-white"
                    }`}
                >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {enabled ? phoneChanged ? "I agree — update number" : "Turn off text alerts" : "I agree — turn on text alerts"}
                </button>
                <span
                    data-testid="account-sms-status"
                    className={`text-[12px] ${enabled ? "text-emerald-300/90" : "text-zinc-500"}`}
                >
                    {prefs == null ? "Loading…" : enabled ? `On · ${prefs.phone}` : "Off"}
                </span>
            </div>
            {prefs?.consent?.at && (
                <p className="text-[11.5px] text-zinc-600" data-testid="account-sms-consent-record">
                    Last change {new Date(prefs.consent.at).toLocaleString()} via {prefs.consent.method.replace(/_/g, " ")}.
                </p>
            )}
            </>}
        </Card>
    );
}

function SecurityCard() {
    const { logout } = useAuth();
    const [busy, setBusy] = useState(false);
    async function signOutEverywhere() {
        setBusy(true);
        try {
            await api.post("/auth/logout-all");
            toast.success("Signed out on every device");
        } catch (_e) {
            /* cookie already gone — fall through to local logout */
        }
        await logout();
    }
    return (
        <Card
            testid="account-security-card"
            icon={ShieldCheck}
            title="Sessions & security"
            subtitle="Lost a phone or shared a laptop? End every active session in one click."
        >
            <div className="flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={signOutEverywhere}
                    disabled={busy}
                    data-testid="account-logout-all-button"
                    className="h-10 px-4 rounded-md border border-zinc-700 bg-zinc-900 text-[13.5px] text-zinc-200 hover:bg-zinc-800 inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                    Sign out of all devices
                </button>
                <Link
                    to="/forgot-password"
                    data-testid="account-change-password-link"
                    className="h-10 px-4 rounded-md border border-zinc-800 text-[13.5px] text-zinc-400 hover:text-zinc-100 inline-flex items-center"
                >
                    Change password
                </Link>
            </div>
        </Card>
    );
}

function DataCard() {
    return (
        <Card
            testid="account-data-card"
            icon={Download}
            title="Your data"
            subtitle="Download everything in your workspace — profile, leads, requests, activity and photo records — as JSON."
        >
            <div className="flex flex-wrap gap-3">
                <a
                    href={`${API}/account/export`}
                    data-testid="account-export-link"
                    className="h-10 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13.5px] font-medium hover:bg-white inline-flex items-center gap-2"
                >
                    <Download className="h-4 w-4" /> Export my data
                </a>
            </div>
            <p className="text-[12px] text-zinc-500">
                To close your account, email{" "}
                <a href="mailto:privacy@webjesters.com" className="text-zinc-300 underline">privacy@webjesters.com</a>. Data is
                kept for 30 days in case you change your mind, then permanently deleted.
            </p>
        </Card>
    );
}

function LegalCard({ user }) {
    return (
        <Card
            testid="account-legal-card"
            icon={FileText}
            title="Legal"
            subtitle={
                user?.terms_accepted_at
                    ? `You accepted version ${user.terms_accepted_version} on ${new Date(user.terms_accepted_at).toLocaleDateString()}.`
                    : "Our agreements with you, in plain English."
            }
        >
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13.5px]">
                <Link to="/privacy" data-testid="account-privacy-link" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline">Privacy Policy</Link>
                <Link to="/terms" data-testid="account-terms-link" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline">Terms of Service</Link>
                <Link to="/dpa" data-testid="account-dpa-link" className="text-zinc-300 hover:text-white underline-offset-4 hover:underline">Data Processing Addendum</Link>
            </div>
        </Card>
    );
}

export default function Account() {
    const { user } = useAuth();
    return (
        <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-4xl" data-testid="account-page">
            <Toaster theme="dark" position="bottom-right" richColors />
            <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500">Account</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">Account &amp; privacy</h1>
            <p className="mt-2 text-[14px] text-zinc-400">
                Signed in as <span className="text-zinc-200" data-testid="account-email">{user?.email}</span>
            </p>
            <div className="mt-10 grid gap-5">
                {user?.role === "admin" && <MfaCard />}
                {user?.role !== "admin" && <SmsAlertsCard />}
                <SecurityCard />
                <DataCard />
                <LegalCard user={user} />
            </div>
        </div>
    );
}
